import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Check } from 'lucide-react'
import type { AppDispatch } from '@/store'
import { sendPhoneCodeThunk, verifyPhoneCodeThunk } from '@/redux/userSlice'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { INPUT_CLASSES } from '@/lib/theme'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'
import athleteApi from '@/lib/api'
import SetupShell, { SetupCard, SectionTitle } from '@/components/setup/SetupShell'

type Address = { line1: string; line2: string; city: string; state: string; postal_code: string }

const Unlocks: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-xs font-bold text-[#4d7c0f]">{children}</span>
)

const PayoutAndAddress: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { athlete, refreshUser } = useAuth()

  // Phone
  const [phone, setPhone] = useState(athlete?.phone ?? '')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [phoneBusy, setPhoneBusy] = useState(false)
  const phoneVerified = !!athlete?.phone_verified

  // Address
  const saved = (athlete as any)?.mailing_address as Partial<Address> | null | undefined
  const [address, setAddress] = useState<Address>({
    line1: saved?.line1 ?? '',
    line2: saved?.line2 ?? '',
    city: saved?.city ?? '',
    state: saved?.state ?? '',
    postal_code: saved?.postal_code ?? '',
  })
  const addressComplete = !!(address.line1 && address.city && address.state && address.postal_code)
  const addressDirty =
    address.line1 !== (saved?.line1 ?? '') ||
    address.line2 !== (saved?.line2 ?? '') ||
    address.city !== (saved?.city ?? '') ||
    address.state !== (saved?.state ?? '') ||
    address.postal_code !== (saved?.postal_code ?? '')
  const [saving, setSaving] = useState(false)

  // Bank
  const hasBank = (athlete?.paymentMethods?.length ?? 0) > 0
  const [bankBusy, setBankBusy] = useState(false)

  const sendCode = async () => {
    if (phone.replace(/\D/g, '').length < 10) {
      toast.error('Invalid phone number', { description: 'Please enter a valid phone number.' })
      return
    }
    setPhoneBusy(true)
    try {
      await dispatch(sendPhoneCodeThunk(phone)).unwrap()
      setCodeSent(true)
      toast('Code sent', { description: `We texted a 6-digit code to ${phone}` })
    } catch (err: any) {
      toast.error('Error', { description: err || 'Failed to send the code.' })
    } finally {
      setPhoneBusy(false)
    }
  }

  const verify = async () => {
    if (code.length < 6) return
    setPhoneBusy(true)
    try {
      await dispatch(verifyPhoneCodeThunk({ phone, code })).unwrap()
      toast('Phone confirmed', { description: 'Payouts are unlocked.' })
    } catch (err: any) {
      toast.error('Verification failed', { description: err || 'Invalid code.' })
    } finally {
      setPhoneBusy(false)
    }
  }

  const setUpBank = async () => {
    setBankBusy(true)
    try {
      const res = await athleteApi.post('/athlete/stripe/connect')
      const url = res.data?.url ?? res.data?.onboardingUrl
      if (url) window.location.href = url
      else throw new Error('No onboarding link returned')
    } catch (err: any) {
      toast.error('Could not start bank setup', {
        description: err?.response?.data?.error ?? err.message,
      })
      setBankBusy(false)
    }
  }

  const saveAndContinue = async () => {
    if (addressDirty && addressComplete) {
      setSaving(true)
      try {
        await athleteApi.patch('/users/me', { mailing_address: address })
        await refreshUser()
      } catch (err: any) {
        toast.error('Could not save your address', {
          description: err?.response?.data?.error ?? 'Please try again.',
        })
        setSaving(false)
        return
      }
      setSaving(false)
    }
    navigate('/brand-view')
  }

  const field = (
    k: keyof Address,
    placeholder: string,
    extra: React.ComponentProps<typeof Input> = {},
  ) => (
    <Input
      value={address[k]}
      onChange={(e) => setAddress((a) => ({ ...a, [k]: e.target.value }))}
      placeholder={placeholder}
      className={INPUT_CLASSES.dark}
      {...extra}
    />
  )

  return (
    <SetupShell
      title="Payout and address"
      subtitle={
        <div>
          <SectionTitle>Get ready to be paid</SectionTitle>
          <p className="mt-1 text-sm text-gray-600">
            Two quick steps. Each one turns something on.
          </p>
        </div>
      }
      footer={
        <Button
          variant="lime"
          className="h-12 w-full text-base"
          onClick={saveAndContinue}
          disabled={saving || (addressDirty && !addressComplete && address.line1 !== '')}
        >
          {saving ? 'Saving…' : 'Save and back to your Content Shop'}
        </Button>
      }
      footnote="Brands ship products to your address. Payout setup can finish later."
    >
      {/* Phone */}
      <SetupCard>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-foreground">Confirm your phone</h3>
          {phoneVerified ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#4d7c0f]">
              <Check className="h-3.5 w-3.5" strokeWidth={3} /> Confirmed
            </span>
          ) : (
            <Unlocks>Unlocks payouts</Unlocks>
          )}
        </div>
        {!phoneVerified ? (
          <div className="mt-3 space-y-2">
            <div className="flex gap-2">
              <Input
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                placeholder="Phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={INPUT_CLASSES.dark}
                disabled={phoneBusy || codeSent}
              />
              <Button
                variant="lime"
                className="shrink-0"
                onClick={sendCode}
                disabled={phoneBusy || !phone}
              >
                {codeSent ? 'Resend' : 'Send code'}
              </Button>
            </div>
            {codeSent ? (
              <div className="flex gap-2">
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="6 digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className={INPUT_CLASSES.dark}
                  disabled={phoneBusy}
                />
                <Button
                  variant="lime"
                  className="shrink-0"
                  onClick={verify}
                  disabled={phoneBusy || code.length < 6}
                >
                  Confirm
                </Button>
              </div>
            ) : (
              <Input placeholder="6 digit code" className={INPUT_CLASSES.dark} disabled />
            )}
          </div>
        ) : (
          <p className="mt-1 text-sm text-gray-600">{athlete?.phone}</p>
        )}
      </SetupCard>

      {/* Address */}
      <SetupCard>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-foreground">Mailing address</h3>
          {saved && !addressDirty ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#4d7c0f]">
              <Check className="h-3.5 w-3.5" strokeWidth={3} /> Saved
            </span>
          ) : (
            <Unlocks>Unlocks gifting</Unlocks>
          )}
        </div>
        <div className="mt-3 space-y-2">
          {field('line1', 'Street address', { autoComplete: 'address-line1' })}
          {field('line2', 'Apt, suite (optional)', { autoComplete: 'address-line2' })}
          <div className="grid grid-cols-[1fr_72px_96px] gap-2">
            {field('city', 'City', { autoComplete: 'address-level2' })}
            {field('state', 'State', { autoComplete: 'address-level1', maxLength: 2 })}
            {field('postal_code', 'Zip', { autoComplete: 'postal-code', inputMode: 'numeric' })}
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Only used to ship products from brands you say yes to.
        </p>
      </SetupCard>

      {/* Bank */}
      <SetupCard className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-foreground">Bank account</h3>
          <p className="mt-0.5 text-xs text-gray-600">
            {hasBank
              ? 'Connected through Stripe.'
              : 'Handled by Stripe. Add it any time before your first payout.'}
          </p>
        </div>
        {hasBank ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#4d7c0f]">
            <Check className="h-3.5 w-3.5" strokeWidth={3} /> Added
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 rounded-full border-foreground/25 bg-transparent text-foreground hover:bg-foreground/10"
            onClick={setUpBank}
            disabled={bankBusy}
          >
            {bankBusy ? 'Opening…' : 'Set up'}
          </Button>
        )}
      </SetupCard>
    </SetupShell>
  )
}

export default PayoutAndAddress
